import { useTranslation } from 'react-i18next';
import { Mail, Minus, Plus, Sparkles, X, Icon } from '../../../ui/Icon';
import BirthDateInput from '../../../ui/BirthDateInput';
import { UserAvatar } from '../../../ui/UserAvatar';
import type { TravellerDraft } from '../types';

interface Props {
  travellers: TravellerDraft[];
  max: number;
  onCountChange: (count: number) => void;
  onUpdate: (key: string, patch: Partial<TravellerDraft>) => void;
  onRemove: (key: string) => void;
}

/**
 * «Who's coming?» — every row becomes a real traveller of the trip (ADR-0021),
 * so the expenses, flights and suitcase can be shared from day one. The date
 * of birth is optional but the step says what it buys (ADR-0029).
 */
export default function TravellersStep({ travellers, max, onCountChange, onUpdate, onRemove }: Props) {
  const { t } = useTranslation();
  const count = travellers.length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 rounded-2xl bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 px-4 py-3">
        <span className="font-serif text-xl text-slate-900 dark:text-slate-100">
          {t('dashboard.newTripWizard.travellersCount', { count })}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button" className="btn-icon w-9 h-9 border border-slate-200 dark:border-slate-700 disabled:opacity-40"
            disabled={count <= 1} onClick={() => onCountChange(count - 1)}
            aria-label={t('dashboard.newTripWizard.travellersFewer')}
          >
            <Icon as={Minus} size={16} />
          </button>
          <span className="w-6 text-center font-semibold tabular-nums" aria-live="polite">{count}</span>
          <button
            type="button" className="btn-icon w-9 h-9 border border-slate-200 dark:border-slate-700 disabled:opacity-40"
            disabled={count >= max} onClick={() => onCountChange(count + 1)}
            aria-label={t('dashboard.newTripWizard.travellersMore')}
          >
            <Icon as={Plus} size={16} />
          </button>
        </div>
      </div>

      <ul className="space-y-2.5">
        {travellers.map((tr, i) => {
          const isYou = i === 0;
          const fallbackName = t('dashboard.newTripWizard.travellerDefaultName', { number: i + 1 });
          return (
            <li key={tr.key} className="rounded-2xl bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 p-3.5 space-y-3">
              <div className="flex items-center gap-3">
                <UserAvatar name={tr.name || fallbackName} size={34} />
                {isYou ? (
                  <p className="flex-1 min-w-0 text-[15px] font-semibold text-slate-900 dark:text-slate-100 truncate">
                    {tr.name} <span className="font-normal text-slate-400">· {t('dashboard.newTripWizard.you')}</span>
                  </p>
                ) : (
                  <input
                    type="text" className="input flex-1" maxLength={60}
                    value={tr.name}
                    placeholder={fallbackName}
                    aria-label={t('dashboard.newTripWizard.travellerName')}
                    onChange={e => onUpdate(tr.key, { name: e.target.value })}
                  />
                )}
                {i > 0 && (
                  <button type="button" onClick={() => onRemove(tr.key)} className="btn-icon w-8 h-8 text-slate-400 hover:text-red-500" aria-label={t('dashboard.newTripWizard.travellerRemove')}>
                    <Icon as={X} size={15} />
                  </button>
                )}
              </div>

              <div className="sm:pl-[46px] space-y-2">
                <label className="label !mb-1" htmlFor={`bd-${tr.key}`}>{t('dashboard.newTripWizard.birthDateLabel')}</label>
                <BirthDateInput id={`bd-${tr.key}`} value={tr.birthDate} onChange={v => onUpdate(tr.key, { birthDate: v })} />

                {!isYou && (tr.showEmail ? (
                  <div className="pt-1">
                    <label className="label !mb-1" htmlFor={`em-${tr.key}`}>{t('dashboard.newTripWizard.emailLabel')}</label>
                    <input
                      id={`em-${tr.key}`} type="email" className="input" maxLength={255}
                      placeholder={t('dashboard.newTripWizard.emailPlaceholder')}
                      value={tr.email}
                      onChange={e => onUpdate(tr.key, { email: e.target.value })}
                    />
                    <p className="text-[12px] text-slate-400 mt-1">{t('dashboard.newTripWizard.emailHint')}</p>
                  </div>
                ) : (
                  <button
                    type="button" onClick={() => onUpdate(tr.key, { showEmail: true })}
                    className="flex items-center gap-1.5 text-[13px] font-medium text-teal-600 dark:text-teal-400 hover:text-teal-700 cursor-pointer"
                  >
                    <Icon as={Mail} size={14} />
                    {t('dashboard.newTripWizard.addEmail')}
                  </button>
                ))}
              </div>
            </li>
          );
        })}
      </ul>

      <p className="flex items-start gap-2 text-[13px] leading-relaxed text-slate-500 dark:text-slate-400 bg-teal-50/70 dark:bg-teal-900/20 rounded-xl px-3.5 py-3">
        <Icon as={Sparkles} size={15} className="text-teal-500 shrink-0 mt-0.5" />
        {t('dashboard.newTripWizard.birthDateHint')}
      </p>
    </div>
  );
}
